import type {
  TCampaignsFormData,
  TCampaignWhatsappDispatchFormData,
} from "./campaigns.schema";
import type {
  TCampaign,
  TCampaignCreatePayload,
  TCampaignUpdatePayload,
  TCampaignWhatsappDispatchPayload,
  TWhatsappTemplateBodyNamedParam,
} from "./campaigns.types";
import { TCampaignChannel, TCampaignStatus } from "./campaigns.types";

const WHATSAPP_NAMED_BODY_LINE = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*(.*)$/;

export function parseWhatsappTemplateBodyParamsFromLines(
  lines: string[],
):
  | { positional: string[] }
  | { named: TWhatsappTemplateBodyNamedParam[] }
  | null {
  if (lines.length === 0) {
    return null;
  }
  const allNamed = lines.every((l) => WHATSAPP_NAMED_BODY_LINE.test(l));
  if (allNamed) {
    return {
      named: lines.map((l) => {
        const m = l.match(WHATSAPP_NAMED_BODY_LINE)!;
        return { parameter_name: m[1], text: m[2].trim() };
      }),
    };
  }
  return { positional: lines };
}

export function campaignBodyParamsLinesFromCampaign(
  campaign: Pick<
    TCampaign,
    "whatsapp_template_body_parameters" | "whatsapp_template_body_named_parameters"
  >,
): string {
  if (campaign.whatsapp_template_body_named_parameters?.length) {
    return campaign.whatsapp_template_body_named_parameters
      .map((p) => `${p.parameter_name}=${p.text}`)
      .join("\n");
  }
  if (campaign.whatsapp_template_body_parameters?.length) {
    return campaign.whatsapp_template_body_parameters.join("\n");
  }
  return "";
}

export const getCampaignStatusBadgeClass = (status: TCampaignStatus): string => {
  if (status === "active") {
    return "border border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  }

  if (status === "paused") {
    return "border border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
  }

  return "border border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
};

export const getCampaignChannelBadgeClass = (
  channel: TCampaignChannel
): string => {
  if (channel === "whatsapp") {
    return "border border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  }

  return "border border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300";
};

/** Nova campanha: sem configuração Meta no formulário (preenchida depois / no disparo). */
export const mapCampaignFormToCreatePayload = (
  values: TCampaignsFormData,
  organizationId: string | undefined
): TCampaignCreatePayload => ({
  name: values.name,
  project_id: values.project_id,
  plan_ids: values.plan_ids,
  channel: values.channel,
  status: values.status,
  organization_id: organizationId || values.organization_id,
  whatsapp_meta_config_id: null,
  whatsapp_sender_phone_number_id: null,
  whatsapp_template_name: null,
  whatsapp_template_language: null,
});

/** Atualização parcial: não envia campos WhatsApp para não sobrescrever template/Meta já salvos. */
export const mapCampaignFormToUpdatePayload = (
  values: TCampaignsFormData,
  organizationId: string | undefined
): TCampaignUpdatePayload => ({
  name: values.name,
  project_id: values.project_id,
  plan_ids: values.plan_ids,
  channel: values.channel,
  status: values.status,
  organization_id: organizationId || values.organization_id,
});

export const mapWhatsappDispatchFormToPayload = (
  values: TCampaignWhatsappDispatchFormData
): TCampaignWhatsappDispatchPayload => {
  const lines =
    values.whatsapp_template_body_params_lines
      ?.split("\n")
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const parsed = parseWhatsappTemplateBodyParamsFromLines(lines);
  const payload: TCampaignWhatsappDispatchPayload = {
    whatsapp_meta_config_id: values.whatsapp_meta_config_id,
    whatsapp_sender_phone_number_id:
      values.whatsapp_sender_phone_number_id.trim(),
    whatsapp_template_name: values.whatsapp_template_name.trim(),
    whatsapp_template_language: values.whatsapp_template_language.trim(),
  };
  const metaOrg = values.whatsapp_meta_organization_id?.trim();
  if (metaOrg) {
    payload.whatsapp_meta_organization_id = metaOrg;
  }
  if (parsed && "named" in parsed) {
    payload.whatsapp_template_body_named_parameters = parsed.named;
  } else if (parsed && "positional" in parsed) {
    payload.whatsapp_template_body_parameters = parsed.positional;
  }
  return payload;
};
